import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core'
import {
  format,
  max,
  mouse,
  range,
  scaleLinear,
  scaleTime,
  select,
  sum,
} from 'd3'
import { toDate, addDays, isBetween, formatDate } from 'app/utils/date'
import { TranslateService } from '@ngx-translate/core'
import { ContextService } from '../../../services'
import { Colors } from '../../config/constants'

@Component({
  standalone: false,
  selector: 'app-bar-chart',
  templateUrl: './bar-chart.component.html',
  styleUrls: ['./bar-chart.component.scss'],
})
export class BarChartComponent implements OnInit {
  _data = []
  defaultConfig = {
    mode: 'stacked',
    colors: [Colors.colorAccent],
    unselectedColor: Colors.colorN30,
  }
  _config: any = { ...this.defaultConfig }
  colorScale = null
  selectedCategory = null

  @ViewChild('tooltip', { static: false }) tooltip: ElementRef
  @ViewChild('svg', { static: true }) svg: ElementRef

  @Input() interactivity = true
  @Input() unit: string
  @Input() dateLimits: any = null

  @Input() isLongLegend = false

  @Input()
  get config() {
    return this._config
  }

  set config(newConfig) {
    newConfig = newConfig || {}
    this._config = { ...this.defaultConfig, ...newConfig }
  }

  @Input()
  get data() {
    return this._data
  }

  set data(value: any[]) {
    if (!value || !value.length) {
      return
    }
    this._data = this.prepareData(value)
    this.draw()
  }

  get categories() {
    // read 'category' as key and 'category_name' as label from here
    return this.data[0] ? this.data[0].values : []
  }

  @Output() categoryClick = new EventEmitter()

  constructor(
    private el: ElementRef,
    private translateService: TranslateService,
    private context: ContextService,
  ) {}

  ngOnInit() {
    if (this.data.length) {
      this.draw()
    }
  }

  orderCategories(categories) {
    return categories.sort(
      (el1, el2) => this.getCategoryOrder(el1) - this.getCategoryOrder(el2),
    )
  }

  prepareData(value: any[]) {
    return value
      .slice()
      .reverse()
      .map((elem) => {
        elem.date = new Date(elem.start)
        elem.name =
          this.config && this.config.deliveryNames
            ? this.config.deliveryNames[elem.delivery_id]
            : elem.name
        elem.total = Array.isArray(elem.values)
          ? sum(elem.values, (d) => d.value)
          : elem.value
        elem.values = elem.values || [
          { category: 'nvisitors', value: elem.value },
        ]
        elem.values = elem.values.map((v, i, arr) => {
          const prev = arr[i - 1]
          v.date = elem.date
          v.delivery_id = elem.delivery_id
          v.name = elem.name
          v.bottom = prev ? prev.top : 0
          v.top = v.bottom + (v.value || 0)
          v.category_name =
            this.config && this.config.categoryNames
              ? this.config.categoryNames[v.category]
              : v.category
          return v
        })
        if (this._config.filterKey) {
          elem.values = this.orderCategories(elem.values)
        }
        return elem
      })
  }

  selectCategory(category) {
    if (this.interactivity) {
      this.selectedCategory = category
      select(this.svg.nativeElement)
        .selectAll('rect')
        .classed(
          'unselected',
          (d) => this.selectedCategory && d.category !== this.selectedCategory,
        )
    }
  }

  handleClick(d) {
    this.categoryClick.emit(d)
  }

  getCategoryOrder(cat) {
    const subfilters = this.context.vertical.delivery.target.subfilters
    const values = subfilters.find((f) => f.id === this.config.filterKey).values
    return values[cat.category].order || 0
  }

  getColor(cat, idx = 0) {
    return this.config.filterKey &&
      this.categories.length <= this.config.colors.length &&
      this.getCategoryOrder(cat) < this.config.colors.length
      ? this.config.colors[this.getCategoryOrder(cat)]
      : this.colorScale(idx)
  }

  draw() {
    const containerDims = this.el.nativeElement.getClientRects()[0]
    if (!containerDims) {
      console.log(
        '[Bar Chart] Aborting render: container dimensions are not available',
      )
      return
    }
    let tooltip
    if (this.interactivity) {
      tooltip = select(this.tooltip.nativeElement)
    }
    const svg = select(this.svg.nativeElement)
    const margin = {
      top: 16,
      right: 24,
      bottom: this.isLongLegend ? 68 : 56,
      left: 24,
    }
    const width = containerDims.width - margin.left - margin.right
    const height = containerDims.height - margin.top - margin.bottom
    svg.attr('width', containerDims.width).attr('height', containerDims.height)
    svg.selectAll('g').remove()
    const g = svg
      .append('g')
      .attr('class', 'center')
      .attr('transform', `translate(${margin.left}, ${margin.top})`)

    const dateLimits = this.dateLimits || this.config.getDateLimits()
    const granularity = this.config.getCurrentGranularity()
    const xMargin = 60
    const xScale = scaleTime()
      .domain(dateLimits)
      .range([xMargin, width - xMargin])
      .nice()
      .clamp(true)

    const yMax =
      this._config.mode === 'stacked'
        ? max(this.data, (d) => d.total)
        : max(this.data, (d) => max(d.values, (d2) => d2.value))
    const yScale = scaleLinear()
      .rangeRound([height, 0])
      .domain([0, yMax])
      .nice()

    this._data = this._data
      .filter((d) => {
        const date = toDate(d.start)
        const pad = granularity === 'day' ? 0 : 1
        return isBetween(
          date,
          addDays(dateLimits[0], -pad),
          addDays(dateLimits[1], pad),
        )
      })
      .map((d) => {
        d.values = d.values.map((v, i, arr) => {
          const prev = arr[i - 1]
          v.bottom = prev ? prev.top : 0
          v.top = v.bottom + (v.value || yScale.invert(height - 2))
          return v
        })
        return d
      })

    const colorScale = scaleLinear()
      .range([this._config.colors[0], this._config.colors.slice(-1)[0]])
      .domain([0, this.categories.length - 1])

    this.colorScale = colorScale

    const bgGapPercent = 0.25
    g.append('g')
      .attr('class', 'y-background')
      .selectAll('line')
      .data(range(0, 1 + bgGapPercent, bgGapPercent))
      .enter()
      .append('line')
      .attr('stroke', Colors.colorN15)
      .attr('x1', 0)
      .attr('x2', width)
      .attr('y1', (d) => Math.floor(d * height))
      .attr('y2', (d) => Math.floor(d * height))

    const stepWidth =
      (width - 2 * xMargin) /
      (xScale.ticks(this.config.getTickInterval()).length - 1)
    // used for the left and right margin of each bar-group
    const barMargin = 5
    const barWidth = stepWidth - 2 * barMargin
    // inner vars only used in mode 'grouped'
    const barWidthInner = barWidth / this.categories.length
    // used for the left and right margin of each inner-bar in mode 'grouped'
    const barMarginInner = 3

    const barGroups = g
      .append('g')
      .attr('class', 'bars')
      .selectAll('g')
      .data(this.data)
      .enter()
      .append('g')
      .attr(
        'transform',
        (d) => `translate(${xScale(d.date) - stepWidth / 2 + barMargin}, 0)`,
      )
      .attr('class', (d) => `bar-group-${d.name}`)

    const rects = barGroups
      .selectAll('rect')
      .data((d) => d.values)
      .enter()
      .append('rect')
      .attr('class', (d) => `inner-bar ${d.category}`)
      .attr('stroke-width', '0.2px')
      .attr('stroke', 'white')
      .attr('fill', (d, i) => this.getColor(d, i))
      .on('click', (d) => this.handleClick(d))
      .on('mouseenter', (d, i, nodes) => {
        const rect = select(nodes[i])
        const path = select(nodes[i].parentElement).select('.hover-path')
        const x = +rect.attr('x') + 1,
          y = +rect.attr('y') + 1,
          w = +rect.attr('width') - 2,
          h = +rect.attr('height') - 2
        path.style('display', null)
        path.attr('d', `M${x} ${y} h ${w} v ${h} h ${-w} L ${x} ${y}`)

        if (this.interactivity) {
          // TODO: add the "go to map" functionality.
          tooltip
            .select('.name')
            .text(this.translateService.instant(d.name) + ' ')
          tooltip
            .select('.category')
            .text(this.translateService.instant(d.category_name))
          tooltip.select('.value').text(format(',.0f')(d.value))
          tooltip
            .select('.unit')
            .text(
              ` ${this.translateService.instant(
                this.config.useUnit ? this.unit : d.category_name.toLowerCase(),
              )}`,
            )
          tooltip.classed('hidden', false)
        }
      })
      .on('mousemove', (d, i, nodes) => {
        const mousePos = mouse(nodes[i])
        const x = xScale(d.date) + mousePos[0]
        if (this.interactivity) {
          tooltip.style('left', `${x}px`)
        }
      })
      .on('mouseout', (d, i, nodes) => {
        const path = select(nodes[i].parentElement).select('.hover-path')
        path.style('display', 'none')
        if (this.interactivity) {
          tooltip.classed('hidden', true)
        }
      })

    if (this.interactivity) {
      barGroups
        .append('path')
        .attr('fill', 'none')
        .attr('stroke', Colors.colorN80)
        .attr('stroke-width', '2px')
        .attr('stroke-linecap', 'round')
        .attr('class', 'hover-path')
        .style('pointer-events', 'none')
    }

    if (this._config.mode === 'stacked') {
      rects
        .attr('y', (d) => yScale(d.top))
        .attr('x', 0)
        .attr('height', (d) =>
          Math.max(2, yScale(d.bottom) - yScale(d.top) - 0.2),
        )
        .attr('width', barWidth)
    } else if (this._config.mode === 'grouped') {
      rects
        .attr('width', barWidthInner - barMarginInner * 2)
        .attr('height', (d) => height - yScale(d.value))
        .attr('y', (d) => yScale(d.value))
        .attr('x', (d, i) => barMarginInner + i * barWidthInner)
    } else {
      throw new Error(
        `Unsupported bar-chart mode: ${this._config.mode}. Supported values are 'grouped' and 'stacked'`,
      )
    }

    g.append('g')
      .attr('class', 'x axis')
      .attr('transform', `translate(0, ${height})`)
      .selectAll('text')
      .data(xScale.ticks(this.config.getTickInterval()))
      .enter()
      .append('text')
      .attr('text-anchor', 'middle')
      .attr('x', (d) => xScale(d))
      .attr('dy', '20px')
      .text((d) => {
        const locale = this.translateService.currentLang
        const date = granularity === 'week' ? addDays(d, 1) : new Date(d)
        let opts: Intl.DateTimeFormatOptions = {
          day: '2-digit',
          month: 'short',
        }
        if (date.getDate() === 1) {
          opts =
            date.getMonth() === 0
              ? { month: 'short', year: 'numeric' }
              : { month: 'short' }
        }
        const label = formatDate(date, opts, locale)
        return label.charAt(0).toUpperCase() + label.slice(1).replace('.', '')
      })

    g.append('g')
      .attr('class', 'y axis')
      .attr('transform', `translate(${width}, 0)`)
      .selectAll('text')
      .data([yMax, 0])
      .enter()
      .append('text')
      .attr('text-anchor', 'end')
      .attr('y', (d, i) => i * height)
      .attr('dy', '-6px')
      .html((d) =>
        d === 0
          ? d
          : `${format(',.0f')(d)}
      <tspan class="light">
        ${
          this.config.showTotalUnit
            ? this.translateService.instant(this.unit)
            : ''
        }
      </tspan>`,
      )
  }
}
